import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-mui";
import { useState } from "react";
import { api, getErrorMessage, type MicroApp } from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import { userHasOrgScope } from "../../../auth/scopes";
import ConfirmDialog from "../../../components/ConfirmDialog.tsx";
import { useAuth } from "../../../contexts/AuthContext.tsx";
import { useOrganization } from "../../../contexts/OrganizationContext.tsx";
import EmptyTableRow from "../../../components/table/EmptyTableRow.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";

/**
 * Manage which user groups may see one restricted micro app. Candidate groups are
 * the currently-selected organization's groups; the backend refuses a group whose
 * org has no access to the app's tenant (400).
 */
function MicroAppGrantsDialog({ app, onClose }: { app: MicroApp | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { selectedOrgId, currentOrg } = useOrganization();
  const canManageGroups = userHasOrgScope(user, selectedOrgId, "org:manage_groups");
  const [groupId, setGroupId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<{ group_id: string; name: string } | null>(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: keys.microAppGrant.byApp(app?.id) });
    queryClient.invalidateQueries({ queryKey: keys.microApp.all });
  };

  const { data: grants } = useQuery({
    queryKey: keys.microAppGrant.byApp(app?.id),
    queryFn: () => api.getMicroAppGrants(app!.id),
    enabled: !!app,
  });

  const { data: allGroups } = useQuery({
    queryKey: keys.userGroup.list(selectedOrgId ?? ""),
    queryFn: () => api.getUserGroups(selectedOrgId!),
    enabled: !!app && !!selectedOrgId && canManageGroups,
  });

  const add = useMutation({
    mutationFn: () => api.addMicroAppGrant(app!.id, groupId),
    onSuccess: () => {
      setGroupId("");
      setError(null);
      invalidate();
    },
    onError: (e) => setError(getErrorMessage(e)),
    meta: { suppressErrorToast: true },
  });

  const remove = useMutation({
    mutationFn: (gid: string) => api.removeMicroAppGrant(app!.id, gid),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError(getErrorMessage(e)),
    onSettled: () => setRevoking(null),
    meta: { suppressErrorToast: true },
  });

  const grantedIds = new Set((grants ?? []).map((g) => g.group_id));
  const candidates = (allGroups ?? []).filter((g) => !grantedIds.has(g.id));

  return (
    <Dialog open={!!app} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Access: {app?.title}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {app?.access_mode === "restricted"
            ? "This app is restricted: only members of the groups below can see it."
            : "This app is visible to everyone in its tenant. Group grants take effect once you switch it to Restricted."}
        </Typography>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}

        {!selectedOrgId || !canManageGroups ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {selectedOrgId
              ? `You must be an admin of ${currentOrg?.name ?? "this organization"} to grant its groups.`
              : "Switch to an organization you administer to grant its groups."}
          </Alert>
        ) : (
          <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: "center" }}>
            <TextField
              select
              size="small"
              label="Grant a group"
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
              sx={{ minWidth: 260 }}
            >
              {candidates.map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {g.name}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              loading={add.isPending}
              disabled={!groupId}
              onClick={() => add.mutate()}
            >
              Grant
            </Button>
          </Stack>
        )}

        <ScrollTable>
          <TableBody>
            {(grants ?? []).map((g) => (
              <TableRow key={g.group_id} hover>
                <TableCell>
                  <Chip size="small" label={g.name} variant="outlined" />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    aria-label="Revoke access"
                    onClick={() => setRevoking(g)}
                  >
                    <Trash2 fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {(grants ?? []).length === 0 && (
              <EmptyTableRow colSpan={2}>No groups granted yet</EmptyTableRow>
            )}
          </TableBody>
        </ScrollTable>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
      <ConfirmDialog
        open={!!revoking}
        title="Revoke access"
        confirmLabel="Revoke"
        pending={remove.isPending}
        onConfirm={() => revoking && remove.mutate(revoking.group_id)}
        onClose={() => setRevoking(null)}
      >
        Revoke <strong>{revoking?.name}</strong>'s access to {app?.title}? Its members stop seeing
        the app unless another group grants it.
      </ConfirmDialog>
    </Dialog>
  );
}

export default MicroAppGrantsDialog;
